import React from 'react';
import { Field } from 'redux-form';
import { popularCountries, receivingMethodFields } from './constants';
import { ReceivingMethodFormValueType, PaymentMethodProps } from './types/types';
import { AppDispatch } from '../../../../../store.type';
import { getContentSchemaFromContentFul } from '@Redux/actions/common';
import { getFlagValue, isFlagEnabled } from '@Redux/actions/flagsmith';
import {
  BENE_SCHEMA_ENABLE_FLAG,
  BENEFICIARY_FLOW_PAYOUT_METHODS,
  EU_REGION,
  FLAG_URL,
  INDIVIDUAL
} from '@Utils/constants';
import {
  getBeneficiaryCountries,
  setReceivingMethodBankAccountCountryValues
} from '@Redux/actions/new-bene-flow';
import { ReceivingMethodBankAccountCountryObjectType } from '../../../types/redux-types';
import { setPopularCountriesAction } from '@Redux/actions/new-bene-flow/new-bene-flow-action';

export const buildApiUrl = (baseUrl: string, params: Record<string, string | number>) => {
  const url = new URL(baseUrl);
  Object.keys(params)
    .filter((key) => !!params[key])
    .forEach((key) => url.searchParams.append(key, params[key].toString()));
  return url.toString();
};

export const renderReceivingMethodFields = (
  formValues: ReceivingMethodFormValueType,
  dispatch: AppDispatch,
  dropdownRef: React.MutableRefObject<HTMLInputElement | undefined>,
  dropdownValues: ReceivingMethodBankAccountCountryObjectType[],
  popularCountries: ReceivingMethodBankAccountCountryObjectType[],
  disableField = false,
  userAccountCountry?: string
) =>
  receivingMethodFields(
    formValues,
    dispatch,
    dropdownRef,
    dropdownValues,
    popularCountries,
    disableField,
    userAccountCountry
  ).map((field: any) => <Field key={field.name} {...field} />);

export const getCountriesOptions = async (
  list: Record<string, any>[],
  popularCountries: ReceivingMethodBankAccountCountryObjectType[]
) => {
  const popularCountriesIds = new Set<Record<string, any>>(
    popularCountries?.map((country: any) => country.key)
  );
  list = list.filter((item) => !popularCountriesIds.has(item.key));

  const res = [...popularCountries, ...list];
  return res.map((item) => {
    if (item.type !== 'heading') {
      const src = `${FLAG_URL}${item.value.toLowerCase()}.png`;
      return { ...item, image: { avatar: true, src } };
    }
    return item;
  });
};

export const getPopularCountriesFromCMS = async (dispatch: AppDispatch) => {
  const response = await getContentSchemaFromContentFul(
    'pageAddRecipientPersonalInformation',
    'MOBILE_ADD_RECIPIENT_PERSONAL_INFORMATION'
  );

  dispatch(setPopularCountriesAction(response?.popularCountries?.value || popularCountries));
};

export const getListOfCountries = async (
  accountType: string,
  dispatch: AppDispatch,
  popularCountries: ReceivingMethodBankAccountCountryObjectType[],
  userAccountCountry?: string
) => {
  const response = await dispatch(getBeneficiaryCountries({ account_type: accountType }));
  //@ts-ignore
  const accountPopularCountries = popularCountries?.[accountType] || {};
  const countryKey =
    userAccountCountry && EU_REGION.includes(userAccountCountry) ? 'EU' : userAccountCountry;
  //@ts-ignore
  const popularCountriesList =
    accountPopularCountries[countryKey] || accountPopularCountries['DEFAULT'] || [];
  const mergedOptions = (await getCountriesOptions(
    response,
    popularCountriesList
  )) as ReceivingMethodBankAccountCountryObjectType[];

  dispatch(setReceivingMethodBankAccountCountryValues(mergedOptions));
};

export const checkBeneSchemaEnabled = (
  userAccountType: string,
  countryCode: string,
  currencyCode: string
) => {
  if (!countryCode || !currencyCode) return false;
  const flagCountries =
    (isFlagEnabled(BENE_SCHEMA_ENABLE_FLAG) && getFlagValue(BENE_SCHEMA_ENABLE_FLAG)) || [];
  return (
    userAccountType === INDIVIDUAL &&
    flagCountries?.some(
      (item: { country_code: string; currency_code: string }) =>
        item.country_code?.toUpperCase() === countryCode.toUpperCase() &&
        item.currency_code?.toUpperCase() === currencyCode.toUpperCase()
    )
  );
};
export const getBeneficiaryFlowPayoutMethodsResponse = () => {
  const response =
    (isFlagEnabled(BENEFICIARY_FLOW_PAYOUT_METHODS) &&
      getFlagValue(BENEFICIARY_FLOW_PAYOUT_METHODS)) ||
    [];
  return response;
};

export function getFastestPaymentMethod(
  paymentMethods: PaymentMethodProps[]
): PaymentMethodProps | null {
  const timeFrameMapping = getBeneficiaryFlowPayoutMethodsResponse()?.time_frame_mapping;
  if (!paymentMethods?.length || !timeFrameMapping) return null;
  const payoutTimes = paymentMethods.map(
    (method) => method.meta?.notes?.estimated_payout_time || ''
  );

  // If all payout times are identical → no fastest method
  const allTimesIdentical = payoutTimes.every((time) => time === payoutTimes[0]);
  if (allTimesIdentical) return null;

  let fastestMethod: (PaymentMethodProps & { priority: number }) | null = null;

  for (const method of paymentMethods) {
    const payoutTime = method.meta?.notes?.estimated_payout_time || '';
    let priority: number | null = null;

    if (timeFrameMapping[payoutTime] !== undefined) {
      priority = timeFrameMapping[payoutTime];
    } else {
      // Partial match fallback
      for (const key of Object.keys(timeFrameMapping)) {
        if (payoutTime.includes(key)) {
          priority = timeFrameMapping[key];
          break;
        }
      }
    }

    if (priority === null) continue;

    // Track the fastest method (lowest priority number)
    if (!fastestMethod || priority < fastestMethod.priority) {
      fastestMethod = { ...method, priority };
    }
  }

  return fastestMethod ?? null;
}

export function getNewPaymentMethodsForCountryCurrency(
  countryCode?: string,
  currencyCode?: string
): string[] | null {
  const newPayoutMethods = getBeneficiaryFlowPayoutMethodsResponse()?.new_payout_methods;
  if (!newPayoutMethods) return null;
  // Find matching country
  const countryConfig = newPayoutMethods.find(
    (item: { country: string }) => item.country.toUpperCase() === countryCode?.toUpperCase()
  );
  if (!countryConfig) return null;

  // Find matching currency inside the country
  const currencyConfig = countryConfig.currencies.find(
    (cur: { receiving_currency: string }) =>
      cur.receiving_currency.toUpperCase() === currencyCode?.toUpperCase()
  );
  if (!currencyConfig) return null;

  return currencyConfig.new_methods || null;
}

export const transformDropDownValues = (
  dropdownValues: ReceivingMethodBankAccountCountryObjectType[]
) => {
  return dropdownValues.map((item) => ({ ...item, image: undefined }));
};
